import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { theme } from '../theme'
import { splitTerms } from '../search'

export type ComboGroup<T> = {
  label: string
  items: T[]
  // When true, results in this group are eligible for the "owned" side of the
  // owned/catalog split — controls where the optional "+ Create …" row sits
  // (it slots at the top of the first owned group when no owned matches).
  owned?: boolean
}

type FetchState<T> = {
  query: string
  groups: ComboGroup<T>[]
  loading: boolean
}

export type ComboboxApi<T> = {
  groups: ComboGroup<T>[]
  loading: boolean
  highlight: number
  setHighlight: (i: number) => void
  entriesLen: number
  // commit(idx) selects the entry at position idx (counting an optional
  // create row at index 0 when the create slot is visible). Returns true if
  // it selected something.
  commit: (idx: number) => boolean
  onKeyDown: (e: KeyboardEvent) => void
  // Whether the optional "+ Create …" entry is currently shown.
  showCreate: boolean
  // Flat selection index of the create row (sits at the bottom of the owned
  // section), or -1 when not shown.
  createIndex: number
  // When there are results to reuse, the create row renders subdued so it
  // doesn't compete with existing matches. With no results it shows in accent.
  createMuted: boolean
}

export function useCombobox<T>(opts: {
  query: string
  fetcher: (q: string) => Promise<ComboGroup<T>[]>
  active: boolean
  debounceMs?: number
  onPick: (item: T) => void
  onCreateRequest?: (seed: string) => void
  // When provided, the "+ Create" row is suppressed only on an *exact* match
  // (so a substring overlap like "potato" vs "mashed potato mix" still lets you
  // create). Without it we fall back to the legacy gate (any owned match hides
  // create).
  isExactMatch?: (item: T, trimmedQuery: string) => boolean
}): ComboboxApi<T> {
  const { query, fetcher, active, onPick, onCreateRequest, isExactMatch } = opts
  const debounceMs = opts.debounceMs ?? 150
  const [state, setState] = useState<FetchState<T>>({ query: '', groups: [], loading: false })
  const [highlight, setHighlight] = useState(0)
  const reqIdRef = useRef(0)

  useEffect(() => {
    if (!active) return
    setState(s => ({ ...s, loading: true }))
    const handle = window.setTimeout(() => {
      const myReq = ++reqIdRef.current
      fetcher(query).then(groups => {
        if (myReq !== reqIdRef.current) return
        setState({ query, groups, loading: false })
        setHighlight(0)
      }).catch(() => {
        if (myReq !== reqIdRef.current) return
        setState({ query, groups: [], loading: false })
      })
    }, debounceMs)
    return () => window.clearTimeout(handle)
  }, [query, active, debounceMs, fetcher])

  const groups = state.groups
  const trimmed = query.trim()
  const ownedItemsCount = groups.filter(g => g.owned).reduce((n, g) => n + g.items.length, 0)
  const totalItems = groups.reduce((n, g) => n + g.items.length, 0)
  // Suppress create on an exact match (when caller supplies a matcher);
  // otherwise keep the legacy "any owned match hides create" behavior.
  const hasExact = isExactMatch
    ? groups.some(g => g.items.some(it => isExactMatch(it, trimmed)))
    : ownedItemsCount > 0
  const showCreate = !!onCreateRequest && trimmed.length > 0 && !hasExact
  // The create row sits at the bottom of the owned ("yours") section, so its
  // flat selection index is the number of owned items above it.
  const createIndex = showCreate ? ownedItemsCount : -1
  // Subdued when there are matches to reuse; full accent when the list is empty.
  const createMuted = totalItems > 0
  const entriesLen = totalItems + (showCreate ? 1 : 0)

  // Flat selection list — mirrors the visible order: optional create row, then
  // each group's items in order. We resolve commit() through this same map.
  const flatPicks = useMemo(() => {
    const out: T[] = []
    for (const g of groups) for (const it of g.items) out.push(it)
    return out
  }, [groups])

  const commit = (idx: number): boolean => {
    if (idx < 0 || idx >= entriesLen) return false
    if (showCreate && idx === createIndex) {
      onCreateRequest!(trimmed)
      return true
    }
    // Skip over the inserted create slot when mapping to the items list.
    const realIdx = showCreate && idx > createIndex ? idx - 1 : idx
    const item = flatPicks[realIdx]
    if (item == null) return false
    onPick(item)
    return true
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (!active) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(entriesLen - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      commit(highlight)
    }
  }

  return { groups, loading: state.loading, highlight, setHighlight, entriesLen, commit, onKeyDown, showCreate, createIndex, createMuted }
}

// ComboboxList renders the result list (with dividers, optional create row,
// and highlight). The host owns the input and decides the surrounding chrome.
export function ComboboxList<T>({
  api, query, renderItem, itemKey, createLabel, groupHeaders, emptyHint,
}: {
  api: ComboboxApi<T>
  query: string
  renderItem: (item: T, terms: string[]) => ReactNode
  itemKey: (item: T) => string | number
  createLabel?: (seed: string) => string
  // When true, render an uppercase muted label above each non-empty group
  // (used by the modal layout). The inline dropdown layout omits headers.
  groupHeaders?: boolean
  emptyHint?: string
}) {
  const { groups, highlight, setHighlight, commit, showCreate, createMuted, loading } = api
  const terms = splitTerms(query)
  const ownedItemsCount = groups.filter(g => g.owned).reduce((n, g) => n + g.items.length, 0)
  let idx = 0
  const nodes: ReactNode[] = []
  let createRendered = false

  // The create row lives at the bottom of the owned ("yours") section: render it
  // once we leave the owned groups (or at the very end if there is no catalog).
  const renderCreate = () => {
    const myIdx = idx
    const isHL = highlight === myIdx
    nodes.push(
      <div
        key="__create"
        onClick={() => commit(myIdx)}
        onMouseEnter={() => setHighlight(myIdx)}
        style={{
          padding: '8px 10px', cursor: 'pointer',
          background: isHL ? theme.surface2 : 'transparent',
          fontFamily: theme.sans, fontSize: 13,
          color: createMuted ? theme.muted : theme.accentInk,
          fontWeight: createMuted ? 500 : 600,
          // When subdued and sitting above the catalog with no owned matches
          // above it, add a hairline so it doesn't run into the catalog rows.
          borderBottom: createMuted && ownedItemsCount === 0
            ? `1px solid ${theme.line}` : undefined,
        }}
      >
        {(createLabel ?? defaultCreateLabel)(query.trim())}
      </div>
    )
    createRendered = true
    idx++
  }

  let firstNonEmpty = true
  for (const g of groups) {
    if (g.items.length === 0) continue
    if (showCreate && !createRendered && !g.owned) renderCreate()
    if (groupHeaders) {
      nodes.push(
        <div key={`__hdr_${g.label}`} style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 700,
          textTransform: 'uppercase', color: theme.muted,
          padding: firstNonEmpty ? '4px 10px 6px' : '12px 10px 6px',
        }}>
          {g.label} <span style={{ color: theme.muted, opacity: 0.6, fontWeight: 500 }}>· {g.items.length}</span>
        </div>
      )
    } else if (!firstNonEmpty) {
      nodes.push(<div key={`__div_${g.label}`} style={{ borderTop: `1px solid ${theme.line}` }} />)
    }
    firstNonEmpty = false
    for (const it of g.items) {
      const isHL = highlight === idx
      const myIdx = idx
      nodes.push(
        <div
          key={itemKey(it)}
          onClick={() => commit(myIdx)}
          onMouseEnter={() => setHighlight(myIdx)}
          style={{
            cursor: 'pointer',
            background: isHL ? theme.surface2 : 'transparent',
          }}
        >
          {renderItem(it, terms)}
        </div>
      )
      idx++
    }
  }
  // No catalog section (all owned, or no results at all) — create goes last.
  if (showCreate && !createRendered) renderCreate()

  if (nodes.length === 0) {
    return (
      <div style={{
        padding: '12px 10px', fontFamily: theme.sans, fontSize: 12,
        color: theme.muted, fontStyle: 'italic',
      }}>
        {loading ? 'Searching…' : (emptyHint ?? 'Type to search…')}
      </div>
    )
  }

  return <>{nodes}</>
}

function defaultCreateLabel(seed: string) {
  return `+ Create “${seed}”…`
}
